import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Navigation, MapPin, Clock, Car, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { id: 'en_route', label: 'En Route', icon: Navigation },
  { id: 'arriving', label: 'Arriving', icon: MapPin },
  { id: 'in_service', label: 'In Service', icon: Car },
  { id: 'complete', label: 'Complete', icon: CheckCircle },
];

const DriverTracker = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [eta, setEta] = useState('');

  const loadData = async () => {
    try {
      const res = await fetch(`${API}/api/tracking/driver/${token}`);
      if (!res.ok) throw new Error('Invalid link');
      const d = await res.json();
      setData(d);
      setSelectedStatus(d.status);
      setEta(d.eta_minutes?.toString() || '');
    } catch { setData(null); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [token]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const body = { status: selectedStatus };
      if (eta) body.eta_minutes = parseInt(eta);
      const res = await fetch(`${API}/api/tracking/driver/${token}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      if (res.ok) {
        toast({ title: 'Status Updated', description: `Set to ${selectedStatus.replace('_', ' ')}` });
        loadData();
      }
    } catch { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;
  if (!data) return <div className="min-h-screen bg-black flex items-center justify-center text-white"><p className="text-lg">Invalid or expired driver link.</p></div>;

  const currentIdx = STEPS.findIndex(s => s.id === data.status);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="driver-tracker">
      <header className="py-4 px-4 border-b border-gray-800">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-red-500 font-semibold tracking-widest">ACE DRIVER</p>
            <h1 className="text-lg font-bold">Booking Tracker</h1>
          </div>
          <span className="text-xs text-gray-500 font-mono">{data.booking_id}</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Booking Info */}
        <Card className="bg-gray-900 border-gray-800 p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500 text-xs">Client</p>
              <p className="font-semibold">{data.client_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Service</p>
              <p className="font-semibold capitalize">{data.service_type?.replace('-', ' ') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Vehicle</p>
              <p className="font-semibold">{data.vehicle_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Pickup</p>
              <p className="font-semibold truncate">{data.pickup_location || 'N/A'}</p>
            </div>
          </div>
        </Card>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-2">
          {STEPS.map((step, i) => {
            const StepIcon = step.icon;
            const isActive = i <= currentIdx;
            const isCurrent = step.id === data.status;
            return (
              <div key={step.id} className="flex flex-col items-center flex-1">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-1 transition-all ${isCurrent ? 'bg-red-600 ring-2 ring-red-400 ring-offset-2 ring-offset-black' : isActive ? 'bg-green-600' : 'bg-gray-800'}`}>
                  <StepIcon className="h-5 w-5 text-white" />
                </div>
                <span className={`text-[10px] text-center ${isCurrent ? 'text-red-500 font-bold' : isActive ? 'text-green-400' : 'text-gray-600'}`}>{step.label}</span>
              </div>
            );
          })}
        </div>

        {/* Status Update Controls */}
        <Card className="bg-gray-900 border-gray-800 p-4 space-y-4" data-testid="driver-controls">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Update Status</h3>
          
          <div>
            <Label className="text-gray-400 text-xs">Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="driver-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STEPS.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-gray-400 text-xs">ETA (minutes)</Label>
            <Input type="number" value={eta} onChange={e => setEta(e.target.value)} placeholder="e.g. 15" className="bg-gray-800 border-gray-700 text-white" data-testid="driver-eta-input" />
          </div>

          <Button onClick={handleUpdate} disabled={updating} className="w-full bg-red-600 hover:bg-red-700 h-12 text-base font-semibold" data-testid="driver-update-btn">
            <Send className="h-4 w-4 mr-2" />
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </Card>

        {/* Quick Status Buttons */}
        <div className="grid grid-cols-2 gap-3">
          {STEPS.filter(s => s.id !== data.status).map(step => (
            <Button key={step.id} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white h-12"
              onClick={() => { setSelectedStatus(step.id); }}
              data-testid={`quick-${step.id}`}>
              <step.icon className="h-4 w-4 mr-2" />{step.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverTracker;
